import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText, Shield, Server, Users, Trash2, AlertTriangle, CheckCircle } from "lucide-react";

export default function AVVertrag() {
  const currentDate = new Date().toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
  
  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <div className="bg-zinc-900 border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-zinc-50 transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zur App
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-yellow-500 rounded-sm flex items-center justify-center">
              <FileText className="w-6 h-6 text-black" />
            </div>
            <div>
              <h1 className="text-3xl font-black tracking-tight text-zinc-50" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Auftragsverarbeitungsvertrag
              </h1>
              <p className="text-zinc-400">gemäß Art. 28 DSGVO</p>
            </div>
          </div>
          <p className="text-sm text-zinc-500">
            Stand: {currentDate} | Version 1.0
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12 space-y-10">
        
        {/* Präambel */}
        <section className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
          <h2 className="text-xl font-bold text-zinc-50 mb-4">Präambel</h2>
          <p className="text-zinc-300 leading-relaxed">
            Dieser Auftragsverarbeitungsvertrag (nachfolgend „AV-Vertrag") wird geschlossen zwischen dem Kunden 
            (nachfolgend „<strong className="text-yellow-500">Verantwortlicher</strong>") und der
          </p>
          <div className="mt-4 p-4 bg-zinc-950 rounded-sm border border-zinc-800">
            <p className="text-zinc-50 font-semibold">manage4yougroup UG (haftungsbeschränkt)</p>
            <p className="text-zinc-400 text-sm">Am Kahlberg 8</p>
            <p className="text-zinc-400 text-sm">91093 Heßdorf</p>
            <p className="text-zinc-400 text-sm">Deutschland</p>
            <p className="text-zinc-400 text-sm mt-2">Telefon: 09135 7189445</p>
            <p className="text-zinc-400 text-sm">E-Mail: sebastian.hotz@manage4yougroup.de</p>
          </div>
          <p className="text-zinc-300 leading-relaxed mt-4">
            (nachfolgend „<strong className="text-yellow-500">Auftragsverarbeiter</strong>") 
            im Rahmen der Nutzung des „Unfallmelde Tool" zur Erfassung und Verwaltung von Arbeitsunfällen, 
            Beinaheunfällen und Verbandbucheinträgen.
          </p>
        </section>

        {/* §1 Gegenstand und Dauer */}
        <section>
          <h2 className="text-xl font-bold text-zinc-50 mb-4 flex items-center gap-3">
            <span className="text-yellow-500">§1</span> Gegenstand und Dauer der Verarbeitung
          </h2>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6 space-y-4 text-zinc-300">
            <div>
              <h3 className="font-semibold text-zinc-50 mb-2">1.1 Gegenstand</h3>
              <p>
                Der Auftragsverarbeiter verarbeitet personenbezogene Daten im Auftrag des Verantwortlichen 
                im Rahmen der Bereitstellung des webbasierten „Unfallmelde Tool" zur Dokumentation und 
                Verwaltung von Arbeitsunfällen gemäß den gesetzlichen Anforderungen des Arbeitsschutzes.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-zinc-50 mb-2">1.2 Dauer</h3>
              <p>
                Die Dauer der Verarbeitung entspricht der Laufzeit des Hauptvertrages über die Nutzung 
                des Unfallmelde Tools. Der AV-Vertrag endet automatisch mit Beendigung des Hauptvertrages.
              </p>
            </div>
          </div>
        </section>

        {/* §2 Art und Zweck */}
        <section>
          <h2 className="text-xl font-bold text-zinc-50 mb-4 flex items-center gap-3">
            <span className="text-yellow-500">§2</span> Art und Zweck der Verarbeitung
          </h2>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6 text-zinc-300">
            <p className="mb-4">Die Verarbeitung umfasst folgende Tätigkeiten:</p>
            <ul className="space-y-2 list-disc list-inside">
              <li>Speicherung von Unfallmeldungen, Beinaheunfällen und Verbandbucheinträgen</li>
              <li>Speicherung von Fotodokumentationen zu Vorfällen</li>
              <li>Verwaltung von Benutzerkonten und Zugriffsrechten</li>
              <li>Statistische Auswertung von Unfalldaten (aggregiert)</li>
              <li>Versand von E-Mail-Benachrichtigungen bei neuen Meldungen</li>
              <li>Export von Daten im CSV-Format auf Anforderung des Verantwortlichen</li>
            </ul>
            <p className="mt-4">
              <strong className="text-zinc-50">Zweck:</strong> Erfüllung der gesetzlichen Dokumentationspflichten 
              im Arbeitsschutz sowie Unterstützung bei der Unfallprävention.
            </p>
          </div>
        </section>

        {/* §3 Art der Daten */}
        <section>
          <h2 className="text-xl font-bold text-zinc-50 mb-4 flex items-center gap-3">
            <span className="text-yellow-500">§3</span> Art der personenbezogenen Daten
          </h2>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-zinc-50 mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4 text-yellow-500" />
                  Daten der Benutzer
                </h3>
                <ul className="text-zinc-300 space-y-1 text-sm list-disc list-inside">
                  <li>Name</li>
                  <li>E-Mail-Adresse</li>
                  <li>Passwort (verschlüsselt/gehasht)</li>
                  <li>Unternehmenszugehörigkeit</li>
                  <li>Berechtigungsstufe</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-zinc-50 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-500" />
                  Daten in Unfallmeldungen
                </h3>
                <ul className="text-zinc-300 space-y-1 text-sm list-disc list-inside">
                  <li>Name des Verunfallten</li>
                  <li>Namen von Zeugen</li>
                  <li>Abteilung/Arbeitsort</li>
                  <li>Datum und Beschreibung des Vorfalls</li>
                  <li>Art der Verletzung und betroffenes Körperteil</li>
                  <li>Fotodokumentation (optional)</li>
                  <li>Anzahl der Fehltage</li>
                </ul>
              </div>
            </div>
            <div className="mt-6 p-4 bg-red-500/10 border border-red-500/30 rounded-sm">
              <p className="text-red-400 text-sm">
                <strong>Besondere Kategorien (Art. 9 DSGVO):</strong> Die verarbeiteten Daten können 
                Gesundheitsdaten enthalten (Art der Verletzung, betroffenes Körperteil). Der Verantwortliche 
                stellt sicher, dass die Verarbeitung auf Grundlage von Art. 9 Abs. 2 lit. b DSGVO 
                (arbeitsrechtliche Pflichten) erfolgt.
              </p>
            </div>
          </div>
        </section>

        {/* §4 Kategorien betroffener Personen */}
        <section>
          <h2 className="text-xl font-bold text-zinc-50 mb-4 flex items-center gap-3">
            <span className="text-yellow-500">§4</span> Kategorien betroffener Personen
          </h2>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6 text-zinc-300">
            <ul className="space-y-2 list-disc list-inside">
              <li>Mitarbeiter des Verantwortlichen (Benutzer des Systems)</li>
              <li>Verunfallte Personen (Mitarbeiter, ggf. Dritte)</li>
              <li>Zeugen von Unfällen</li>
              <li>Sicherheitsbeauftragte und Administratoren</li>
            </ul>
          </div>
        </section>

        {/* §5 Pflichten des Auftragsverarbeiters */}
        <section>
          <h2 className="text-xl font-bold text-zinc-50 mb-4 flex items-center gap-3">
            <span className="text-yellow-500">§5</span> Pflichten des Auftragsverarbeiters
          </h2>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6 space-y-4 text-zinc-300">
            <p>Der Auftragsverarbeiter verpflichtet sich:</p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Personenbezogene Daten nur auf dokumentierte Weisung des Verantwortlichen zu verarbeiten</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Alle Personen, die Zugang zu den Daten haben, zur Vertraulichkeit zu verpflichten</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Alle erforderlichen technischen und organisatorischen Maßnahmen gemäß Art. 32 DSGVO zu ergreifen</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Weitere Auftragsverarbeiter nur mit vorheriger Genehmigung des Verantwortlichen einzusetzen</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Den Verantwortlichen bei der Erfüllung von Betroffenenrechten zu unterstützen</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Datenschutzverletzungen unverzüglich (innerhalb von 24 Stunden) zu melden</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Nach Beendigung der Auftragsverarbeitung alle Daten zu löschen oder zurückzugeben</span>
              </li>
            </ul>
          </div>
        </section>

        {/* §6 Technische und organisatorische Maßnahmen */}
        <section>
          <h2 className="text-xl font-bold text-zinc-50 mb-4 flex items-center gap-3">
            <span className="text-yellow-500">§6</span> Technische und organisatorische Maßnahmen (TOMs)
          </h2>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
            <p className="text-zinc-300 mb-6">
              Der Auftragsverarbeiter hat folgende Maßnahmen gemäß Art. 32 DSGVO implementiert:
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-zinc-950 rounded-sm border border-zinc-800">
                <h4 className="font-semibold text-zinc-50 mb-2 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-yellow-500" />
                  Verschlüsselung
                </h4>
                <ul className="text-zinc-400 text-sm space-y-1">
                  <li>• TLS 1.2/1.3 für Datenübertragung</li>
                  <li>• AES-256 für ruhende Daten</li>
                  <li>• bcrypt für Passwort-Hashing</li>
                </ul>
              </div>
              
              <div className="p-4 bg-zinc-950 rounded-sm border border-zinc-800">
                <h4 className="font-semibold text-zinc-50 mb-2 flex items-center gap-2">
                  <Users className="w-4 h-4 text-yellow-500" />
                  Zugriffskontrolle
                </h4>
                <ul className="text-zinc-400 text-sm space-y-1">
                  <li>• Rollenbasierte Berechtigungen</li>
                  <li>• Multi-Tenant-Datentrennung</li>
                  <li>• JWT-Authentifizierung</li>
                </ul>
              </div>
              
              <div className="p-4 bg-zinc-950 rounded-sm border border-zinc-800">
                <h4 className="font-semibold text-zinc-50 mb-2 flex items-center gap-2">
                  <Server className="w-4 h-4 text-yellow-500" />
                  Verfügbarkeit
                </h4>
                <ul className="text-zinc-400 text-sm space-y-1">
                  <li>• Automatische tägliche Backups</li>
                  <li>• Redundante Serverinfrastruktur</li>
                  <li>• Point-in-Time-Recovery (7 Tage)</li>
                </ul>
              </div>
              
              <div className="p-4 bg-zinc-950 rounded-sm border border-zinc-800">
                <h4 className="font-semibold text-zinc-50 mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-yellow-500" />
                  Nachvollziehbarkeit
                </h4>
                <ul className="text-zinc-400 text-sm space-y-1">
                  <li>• Erstellerzuordnung bei Meldungen</li>
                  <li>• Zeitstempel für alle Einträge</li>
                  <li>• Logging von Systemzugriffen</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* §7 Unterauftragsverarbeiter */}
        <section>
          <h2 className="text-xl font-bold text-zinc-50 mb-4 flex items-center gap-3">
            <span className="text-yellow-500">§7</span> Unterauftragsverarbeiter
          </h2>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6 text-zinc-300">
            <p className="mb-4">
              Der Verantwortliche erteilt hiermit eine allgemeine Genehmigung für den Einsatz der folgenden 
              Unterauftragsverarbeiter. Der Auftragsverarbeiter informiert den Verantwortlichen über 
              Änderungen mit einer Frist von 14 Tagen.
            </p>
            
            <table className="w-full text-sm mt-4">
              <thead>
                <tr className="border-b border-zinc-700">
                  <th className="text-left py-2 text-zinc-400 font-medium">Unternehmen</th>
                  <th className="text-left py-2 text-zinc-400 font-medium">Zweck</th>
                  <th className="text-left py-2 text-zinc-400 font-medium">Standort</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-zinc-800">
                  <td className="py-3 text-zinc-50">MongoDB, Inc.</td>
                  <td className="py-3">Datenbankhosting (MongoDB Atlas)</td>
                  <td className="py-3">EU (Frankfurt/Irland)</td>
                </tr>
                <tr className="border-b border-zinc-800">
                  <td className="py-3 text-zinc-50">Emergent Labs</td>
                  <td className="py-3">Anwendungshosting & Object Storage</td>
                  <td className="py-3">EU</td>
                </tr>
                <tr className="border-b border-zinc-800">
                  <td className="py-3 text-zinc-50">Resend, Inc.</td>
                  <td className="py-3">E-Mail-Versand</td>
                  <td className="py-3">USA (mit SCCs)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* §8 Drittlandtransfer */}
        <section>
          <h2 className="text-xl font-bold text-zinc-50 mb-4 flex items-center gap-3">
            <span className="text-yellow-500">§8</span> Datenübermittlung in Drittländer
          </h2>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6 text-zinc-300">
            <p>
              Eine Übermittlung personenbezogener Daten in Drittländer (außerhalb EU/EWR) erfolgt nur, wenn:
            </p>
            <ul className="mt-4 space-y-2 list-disc list-inside">
              <li>Ein Angemessenheitsbeschluss der EU-Kommission vorliegt (Art. 45 DSGVO)</li>
              <li>Standardvertragsklauseln (SCCs) abgeschlossen wurden (Art. 46 Abs. 2 lit. c DSGVO)</li>
              <li>Andere geeignete Garantien gemäß Art. 46 DSGVO bestehen</li>
            </ul>
            <p className="mt-4">
              Für den E-Mail-Dienst Resend (USA) sind Standardvertragsklauseln abgeschlossen.
            </p>
          </div>
        </section>

        {/* §9 Kontrollrechte */}
        <section>
          <h2 className="text-xl font-bold text-zinc-50 mb-4 flex items-center gap-3">
            <span className="text-yellow-500">§9</span> Kontrollrechte des Verantwortlichen
          </h2>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6 text-zinc-300">
            <p>Der Verantwortliche hat das Recht:</p>
            <ul className="mt-4 space-y-2 list-disc list-inside">
              <li>Vor Beginn und während der Verarbeitung Kontrollen durchzuführen</li>
              <li>Auskünfte und Nachweise über die Einhaltung dieses Vertrages zu verlangen</li>
              <li>Inspektionen vor Ort nach vorheriger Ankündigung (14 Tage) durchzuführen</li>
              <li>Prüfungen durch unabhängige Dritte (Auditoren) durchführen zu lassen</li>
            </ul>
            <p className="mt-4">
              Der Auftragsverarbeiter stellt dem Verantwortlichen alle erforderlichen Informationen 
              zum Nachweis der Einhaltung der Pflichten zur Verfügung.
            </p>
          </div>
        </section>

        {/* §10 Löschung */}
        <section>
          <h2 className="text-xl font-bold text-zinc-50 mb-4 flex items-center gap-3">
            <span className="text-yellow-500">§10</span> Löschung und Rückgabe von Daten
          </h2>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6 text-zinc-300">
            <div className="flex items-start gap-4">
              <Trash2 className="w-6 h-6 text-yellow-500 mt-1 flex-shrink-0" />
              <div>
                <p>
                  Nach Beendigung des Hauptvertrages wird der Auftragsverarbeiter:
                </p>
                <ul className="mt-4 space-y-2 list-disc list-inside">
                  <li>Alle personenbezogenen Daten innerhalb von 30 Tagen löschen</li>
                  <li>Auf Wunsch die Daten vorher in einem gängigen Format (CSV) exportieren und übergeben</li>
                  <li>Die vollständige Löschung schriftlich bestätigen</li>
                  <li>Sicherstellen, dass auch Unterauftragsverarbeiter die Daten löschen</li>
                </ul>
                <p className="mt-4 text-zinc-400 text-sm">
                  Ausnahme: Daten, deren Aufbewahrung gesetzlich vorgeschrieben ist, werden nach Ablauf 
                  der Aufbewahrungsfrist gelöscht.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* §11 Haftung */}
        <section>
          <h2 className="text-xl font-bold text-zinc-50 mb-4 flex items-center gap-3">
            <span className="text-yellow-500">§11</span> Haftung
          </h2>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6 text-zinc-300">
            <p>
              Die Haftung der Parteien richtet sich nach Art. 82 DSGVO. Jede Partei haftet für den Schaden, 
              der durch eine nicht der DSGVO entsprechende Verarbeitung verursacht wurde.
            </p>
            <p className="mt-4">
              Der Auftragsverarbeiter haftet für den durch die Verarbeitung verursachten Schaden nur dann, 
              wenn er seinen speziell auferlegten Pflichten nicht nachgekommen ist oder Anweisungen des 
              Verantwortlichen missachtet hat.
            </p>
          </div>
        </section>

        {/* §12 Schlussbestimmungen */}
        <section>
          <h2 className="text-xl font-bold text-zinc-50 mb-4 flex items-center gap-3">
            <span className="text-yellow-500">§12</span> Schlussbestimmungen
          </h2>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6 text-zinc-300 space-y-4">
            <p>
              <strong className="text-zinc-50">12.1</strong> Änderungen und Ergänzungen dieses Vertrages 
              bedürfen der Schriftform.
            </p>
            <p>
              <strong className="text-zinc-50">12.2</strong> Sollten einzelne Bestimmungen unwirksam sein, 
              bleibt die Wirksamkeit der übrigen Bestimmungen unberührt.
            </p>
            <p>
              <strong className="text-zinc-50">12.3</strong> Es gilt deutsches Recht. Gerichtsstand ist 
              Düsseldorf.
            </p>
            <p>
              <strong className="text-zinc-50">12.4</strong> Dieser Vertrag tritt mit Unterzeichnung durch 
              beide Parteien in Kraft.
            </p>
          </div>
        </section>

        {/* Unterschriften */}
        <section className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
          <h2 className="text-xl font-bold text-zinc-50 mb-6">Unterschriften</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <p className="text-zinc-400 text-sm mb-4">Verantwortlicher (Kunde)</p>
              <div className="border-b border-zinc-600 pb-2 mb-2">
                <p className="text-zinc-500 text-sm">Ort, Datum</p>
              </div>
              <div className="border-b border-zinc-600 pb-2 mb-2 mt-8">
                <p className="text-zinc-500 text-sm">Unterschrift, Name in Druckbuchstaben</p>
              </div>
            </div>
            <div>
              <p className="text-zinc-400 text-sm mb-4">Auftragsverarbeiter</p>
              <div className="border-b border-zinc-600 pb-2 mb-2">
                <p className="text-zinc-500 text-sm">Ort, Datum</p>
              </div>
              <div className="border-b border-zinc-600 pb-2 mb-2 mt-8">
                <p className="text-zinc-500 text-sm">Unterschrift, Name in Druckbuchstaben</p>
                <p className="text-zinc-400 text-xs mt-1">manage4yougroup UG (haftungsbeschränkt)</p>
                <p className="text-zinc-400 text-xs">Am Kahlberg 8, 91093 Heßdorf</p>
              </div>
            </div>
          </div>
        </section>

        {/* Download Hinweis */}
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-sm p-6 text-center">
          <p className="text-yellow-500">
            <strong>Hinweis:</strong> Zum Ausdrucken können Sie diese Seite über Ihren Browser drucken 
            (Strg+P / Cmd+P). Für eine PDF-Version wählen Sie „Als PDF speichern" als Drucker.
          </p>
        </div>

        {/* Footer */}
        <div className="border-t border-zinc-800 pt-8 text-center text-zinc-500 text-sm">
          <p>© {new Date().getFullYear()} manage4yougroup UG (haftungsbeschränkt)</p>
          <p className="mt-2">Dieser AV-Vertrag entspricht den Anforderungen der DSGVO (Art. 28).</p>
        </div>
      </div>
    </div>
  );
}
